import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Upload, ImageIcon } from "lucide-react";

const TZS = (() => {
  let z = [];
  try { z = Intl.supportedValuesOf("timeZone"); } catch { z = []; }
  // Chrome lists the legacy alias Asia/Calcutta; always offer the canonical IANA id Asia/Kolkata
  return Array.from(new Set(["Asia/Kolkata", ...z.filter((x) => x !== "Asia/Calcutta"), "UTC"])).sort();
})();
const tzOffset = (z) => { try { return new Intl.DateTimeFormat("en-US", { timeZone: z, timeZoneName: "longOffset" }).formatToParts(new Date()).find((p) => p.type === "timeZoneName")?.value.replace("GMT", "UTC") || ""; } catch { return ""; } };
export const tzLabel = (z) => (z === "Asia/Kolkata" ? "Asia/Kolkata — India Standard Time (IST, UTC+05:30)" : `${z} (${tzOffset(z) || "UTC"})`);

export function LogoCard({ data }) {
  const qc = useQueryClient();
  const ref = useRef(null);
  const id = data.profile.logo_asset_id;
  const up = useMutation({
    mutationFn: async (f) => { const fd = new FormData(); fd.append("file", f); return api.post("/billing/invoice-profile/logo?branch_id=", fd, { headers: { "Content-Type": "multipart/form-data" } }); },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["business-settings"] }); toast.success("Logo updated"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  return (
    <div className="flex items-center gap-4" data-testid="bs-logo">
      {id ? <img src={`${api.defaults.baseURL}/billing/logo/${id}`} alt="Business logo" className="h-16 max-w-[160px] rounded border border-slate-200 bg-white object-contain p-1" data-testid="bs-logo-img" />
        : <div className="grid h-16 w-16 place-items-center rounded border border-dashed border-slate-300 text-slate-300"><ImageIcon className="h-6 w-6" /></div>}
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="bs-logo-input" onChange={(e) => e.target.files[0] && up.mutate(e.target.files[0])} />
      <div>
        <Button variant="outline" size="sm" className="gap-1.5" disabled={!data.can_upload_logo || up.isPending} onClick={() => ref.current?.click()} data-testid="bs-logo-btn"><Upload className="h-4 w-4" /> {up.isPending ? "Uploading…" : id ? "Replace logo" : "Upload logo"}</Button>
        <p className="mt-1 text-[11px] text-slate-400">PNG, JPG or WebP. Same logo as on new invoices; issued invoices keep theirs.</p>
      </div>
    </div>
  );
}

function Confirm({ open, title, body, onConfirm, onCancel, busy, id }) {
  return (
    <AlertDialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <AlertDialogContent className="bg-white" data-testid={`${id}-confirm`}>
        <AlertDialogHeader><AlertDialogTitle>{title}</AlertDialogTitle><AlertDialogDescription asChild><div className="space-y-2 text-sm text-slate-600">{body}</div></AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter><AlertDialogCancel data-testid={`${id}-confirm-cancel`}>Cancel</AlertDialogCancel><AlertDialogAction disabled={busy} onClick={onConfirm} data-testid={`${id}-confirm-ok`} className="bg-slate-900 text-white">Confirm change</AlertDialogAction></AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function TypesCard({ data }) {
  const qc = useQueryClient();
  const cur = data.tenant.business_types || [];
  const [sel, setSel] = useState(cur);
  const [ask, setAsk] = useState(false);
  const added = sel.filter((x) => !cur.includes(x)), removed = cur.filter((x) => !sel.includes(x));
  const label = (id) => data.business_types_catalog.find((t) => t.id === id)?.label || id;
  const mods = [...new Set(added.flatMap((x) => data.type_modules[x] || []))];
  const save = useMutation({
    mutationFn: () => api.put("/business/types", { business_types: sel, confirm: true }),
    onSuccess: () => { setAsk(false); qc.invalidateQueries({ queryKey: ["business-settings"] }); qc.invalidateQueries({ queryKey: ["tenant"] }); toast.success("Business types updated"); },
    onError: (e) => { setAsk(false); toast.error(apiErr(e)); },
  });
  return (
    <div data-testid="bs-types">
      <div className="flex flex-wrap gap-2">
        {data.business_types_catalog.map((t) => (
          <label key={t.id} className={`flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors ${sel.includes(t.id) ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-slate-200 text-slate-600"}`}>
            <Checkbox checked={sel.includes(t.id)} disabled={!data.can_edit} onCheckedChange={(v) => setSel(v ? [...sel, t.id] : sel.filter((x) => x !== t.id))} data-testid={`bs-type-${t.id}`} />{t.label}
          </label>
        ))}
      </div>
      {data.can_edit && (added.length > 0 || removed.length > 0) && (
        <div className="mt-3 flex gap-2"><Button size="sm" className="bg-slate-900 text-white" disabled={!sel.length} onClick={() => setAsk(true)} data-testid="bs-types-save">Review change</Button><Button size="sm" variant="ghost" onClick={() => setSel(cur)} data-testid="bs-types-cancel">Cancel</Button></div>
      )}
      <Confirm id="bs-types" open={ask} busy={save.isPending} onCancel={() => setAsk(false)} onConfirm={() => save.mutate()} title="Change business types?" body={<>
        {added.length > 0 && <p>Adding: <b>{added.map(label).join(", ")}</b>.{mods.length ? ` This turns on: ${mods.join(", ")}.` : " No extra modules are needed."}</p>}
        {removed.length > 0 && <p>Removing: <b>{removed.map(label).join(", ")}</b>. Modules stay on and no records are deleted — switch modules off separately in Settings if you no longer need them.</p>}
      </>} />
    </div>
  );
}

export function TimezoneCard({ data }) {
  const qc = useQueryClient();
  const current = data.tenant.timezone || "";
  const [tz, setTz] = useState(current || "Asia/Kolkata");
  const [q, setQ] = useState("");
  const [ask, setAsk] = useState(false);
  const save = useMutation({
    mutationFn: () => api.put("/business/timezone", { timezone: tz, confirm: true }),
    onSuccess: () => { setAsk(false); qc.invalidateQueries({ queryKey: ["business-settings"] }); qc.invalidateQueries({ queryKey: ["business-hours"] }); toast.success("Time zone updated"); },
    onError: (e) => { setAsk(false); toast.error(apiErr(e)); },
  });
  const list = TZS.filter((z) => !q || z.toLowerCase().includes(q.toLowerCase()) || (z === "Asia/Kolkata" && /ind|ist|kol|calc|mumbai|delhi/i.test(q)));
  const opts = list.includes(tz) ? list : [tz, ...list];
  return (
    <div data-testid="bs-timezone" className="space-y-2">
      <div className="text-sm text-slate-700" data-testid="bs-timezone-current">Saved: <b>{current ? tzLabel(current) : "Not set"}</b></div>
      {(!current || (current !== "Asia/Kolkata" && data.tenant.currency === "INR")) && <div className="rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-800" data-testid="bs-timezone-check">{current ? `This business bills in INR but its time zone is ${current}. If you operate in India, choose Asia/Kolkata (IST) and confirm.` : "No time zone has been saved yet — times are read as India Standard Time. Pick and confirm your time zone."}</div>}
      <div className="flex flex-wrap items-center gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search time zones (e.g. India, Dubai)" className="h-9 w-full rounded-md border border-slate-200 px-2 text-sm sm:w-56" data-testid="bs-timezone-search" disabled={!data.can_edit} />
        <select value={tz} disabled={!data.can_edit} onChange={(e) => setTz(e.target.value)} data-testid="bs-timezone-select" className="h-9 w-full max-w-full rounded-md border border-slate-200 bg-white px-2 text-sm sm:w-auto">
          {opts.map((z) => <option key={z} value={z}>{tzLabel(z)}</option>)}
        </select>
        {data.can_edit && tz !== current && <Button size="sm" className="bg-slate-900 text-white" onClick={() => setAsk(true)} data-testid="bs-timezone-save">{current ? "Change time zone" : "Save time zone"}</Button>}
      </div>
      <Confirm id="bs-timezone" open={ask} busy={save.isPending} onCancel={() => setAsk(false)} onConfirm={() => save.mutate()} title={`Switch to ${tz}?`} body={<>
        <p>Operating hours and booking-hour checks will be read in <b>{tz}</b> from now on.</p>
        <p>Existing bookings, invoices and payment dates are not changed. Backdating rules and existing reports keep using India time (IST) as before.</p>
      </>} />
    </div>
  );
}
